import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { keys } from "../../api/keys"
import { productWrites, translateWrites } from "../../api/mutations"
import { TranslateSource } from "../../api/types"
import { ActionNotes, Note } from "../../components/Note"
import { useAction } from "../../hooks/useAction"
import { useConfirm } from "../../hooks/useConfirm"
import { APPLIABLE, SAFE_APPLIABLE } from "./rules"

/**
 * «Пополнить и применить» — два конца цикла, между которыми находится сам
 * справочник.
 *
 * Разбор механики — в `СПРАВОЧНИК-ПЕРЕВОДОВ.md`. Коротко: перевод, сохранённый
 * в справочнике, сам по себе никуда не уезжает. Названия групп — исключение,
 * они попадают в рабочие таблицы сразу (`TranslateToSourceProxy`); всё
 * остальное раскладывает по таблицам кнопка «Применить».
 *
 * Четвёртый блок ниже («Пересобрать короткие имена товаров») — не часть
 * цикла 1-2-3, а дубликат кнопки с карточки группы (`group/SiteNameHelp.tsx`):
 * та же `productWrites.generateShortNames()`. Здесь она уместна по той же
 * причине, что и «Применить» — действие глобальное, по всей базе и сразу на
 * всех языках, а не про одну карточку.
 */
export default function SyncPanel({
	languageId,
	languageName,
	isRussian,
	untranslatedDescriptions,
	disabled,
}: {
	languageId: number
	languageName: string
	isRussian: boolean
	/** Непереведённые описания в текущем наборе — их пункты выпадут. */
	untranslatedDescriptions: number
	disabled: boolean
}) {
	const client = useQueryClient()
	const { ask, dialog } = useConfirm()
	const action = useAction()

	const [types, setTypes] = useState<Set<TranslateSource>>(
		() => new Set(SAFE_APPLIABLE)
	)

	const risky = types.has(TranslateSource.PictGroupDesc)

	async function generateNew() {
		const yes = await ask({
			title: "Собрать новые фразы?",
			text: "В справочник добавятся русские строки объектов, которых там ещё нет: новые группы, атрибуты, категории, пиктограммы. Существующие переводы не меняются и не удаляются. Описание группы попадёт сюда, только если у неё проставлена галка «описание проверено» на русском. На большой базе выполняется долго.",
			confirm: "Собрать",
		})
		if (!yes) return
		void action.run(async () => {
			await translateWrites.generateNew()
			await client.invalidateQueries({ queryKey: keys.translates() })
		}, "Справочник пополнен — новые фразы появятся в списке")
	}

	async function apply() {
		if (types.size === 0 || isRussian) return
		const chosen = APPLIABLE.filter(item => types.has(item.value))

		const yes = await ask({
			title: `Применить переводы на ${languageName} к таблицам базы?`,
			text:
				`Разделы: ${chosen.map(item => item.label).join(", ")}. ` +
				"Операция глобальная: каталог она не смотрит и обновляет всю базу на выбранном языке. " +
				(risky
					? "Описания групп бэк собирает заново: русское описание режется по «•», каждый пункт ищется в справочнике по точному совпадению, а ненайденные из описания выбрасываются. " +
						(untranslatedDescriptions > 0
							? `В текущем наборе не переведено таких пунктов: ${untranslatedDescriptions}. `
							: "") +
						"У групп, где переводов не нашлось совсем, описание на этом языке станет пустым — включая описания, набранные руками в карточке. "
					: "") +
				"Отменить одним действием нельзя.",
			confirm: "Применить",
			danger: risky,
		})
		if (!yes) return

		void action.run(async () => {
			await translateWrites.applyToSources(
				languageId,
				chosen.map(item => item.value)
			)
			// названия и описания групп, имена атрибутов и категорий поменялись
			await Promise.all([
				client.invalidateQueries({ queryKey: keys.groups() }),
				client.invalidateQueries({ queryKey: keys.group() }),
				client.invalidateQueries({ queryKey: keys.attributes() }),
				client.invalidateQueries({ queryKey: keys.webCategories() }),
			])
		}, `Переводы применены к базе на ${languageName}. Дальше их подхватит выгрузка на сайт и печатный каталог`)
	}

	async function generateNames() {
		const yes = await ask({
			title: "Пересобрать короткие имена товаров по всей базе?",
			text: "Операция глобальная: пройдёт по всем товарам во всех группах и на всех языках справочника разом, а не только на выбранном. На большой базе выполняется долго. Отменить одним действием нельзя.",
			confirm: "Сгенерировать",
		})
		if (!yes) return
		void action.run(
			() => productWrites.generateShortNames(),
			"Короткие названия успешно сгенерированы"
		)
	}

	return (
		<div className="panel dict-sync">
			<div className="panel__body">
				<p className="muted">
					Справочник сам по себе ничего не публикует — цикл из трёх шагов.
					Названия групп исключение: они попадают в базу сразу при сохранении
					перевода. Описания значений тоже — их подставляют на лету при
					выгрузке на сайт и в Excel каталога.
				</p>

				<ActionNotes action={action} />

				<div className="dict-sync__step">
					<div className="dict-sync__num">1</div>
					<div className="dict-sync__body">
						<b>Собрать новые фразы</b>
						<div className="muted">
							Русские строки объектов, которых в справочнике ещё нет: новые
							группы, атрибуты, категории, пиктограммы. Только добавляет.
							Описание группы попадёт сюда, только если у неё стоит галка
							«описание проверено» на русском.
						</div>
					</div>
					<button
						className="btn"
						disabled={disabled || action.busy}
						onClick={() => void generateNew()}
					>
						Собрать
					</button>
				</div>

				<div className="dict-sync__step">
					<div className="dict-sync__num">2</div>
					<div className="dict-sync__body">
						<b>Перевести и сохранить</b>
						<div className="muted">
							Это сам экран. Перевод ложится в справочник, но в рабочих
							таблицах его ещё нет.
						</div>
					</div>
				</div>

				<div className="dict-sync__step">
					<div className="dict-sync__num">3</div>
					<div className="dict-sync__body">
						<b>Применить переводы к базе</b>
						<div className="muted">
							Раскладывает переводы по таблицам, откуда их берут выгрузка на
							сайт и печатный каталог. Обновляет всю базу на выбранном языке,
							а не только текущий каталог.
						</div>
						<div className="dict-sync__types">
							{APPLIABLE.map(item => (
								<label
									key={item.value}
									className={
										"dict-check" + (item.risky ? " dict-check--risky" : "")
									}
									title={item.hint}
								>
									<input
										type="checkbox"
										checked={types.has(item.value)}
										onChange={() =>
											setTypes(state => {
												const next = new Set(state)
												if (next.has(item.value)) next.delete(item.value)
												else next.add(item.value)
												return next
											})
										}
									/>
									{item.label}
								</label>
							))}
						</div>
						{risky && (
							<Note tone="hint">
								Описание собирается заново: пункты без перевода из него
								выбрасываются
								{untranslatedDescriptions > 0 &&
									` (в текущем наборе таких ${untranslatedDescriptions})`}
								, а у групп, где переводов не нашлось совсем, описание на этом
								языке станет пустым — включая набранное руками в карточке.
							</Note>
						)}
					</div>
					<button
						className="btn btn--primary"
						disabled={disabled || action.busy || types.size === 0 || isRussian}
						title={
							isRussian
								? "Выберите в шапке язык перевода"
								: "Записать переводы в рабочие таблицы"
						}
						onClick={() => void apply()}
					>
						Применить
					</button>
				</div>

				<div className="dict-sync__step dict-sync__step--extra">
					<div className="dict-sync__num" title="Отдельное действие, не часть цикла 1-2-3 выше">
						↻
					</div>
					<div className="dict-sync__body">
						<b>Пересобрать короткие имена товаров</b>
						<div className="muted">
							Та же кнопка, что и на карточке группы, вкладка «Табличная
							часть» — продублирована сюда, потому что действие не про одну
							карточку: оно проходит по всей базе и сразу на всех языках
							справочника, как «Применить» выше. Из важных атрибутов (уже
							переведённых здесь) собирает короткое имя товара для сайта —
							название группы → важные атрибуты → торговая марка → артикул.
						</div>
					</div>
					<button
						className="btn btn--warn"
						disabled={disabled || action.busy}
						title="Пересобрать короткие имена всех товаров по всей базе на всех языках"
						onClick={() => void generateNames()}
					>
						Сгенерировать
					</button>
				</div>

				{dialog}
			</div>
		</div>
	)
}
